import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Banner,
  Badge,
  Box,
  Button,
  Card,
  DatePicker,
  Divider,
  Icon,
  IndexTable,
  Modal,
  Popover,
  Text,
  TextField,
} from "@shopify/polaris";
import { CalendarMinor } from "@shopify/polaris-icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  gettotalPossibleOrders,
  historicalOrdersQueue,
  historicalOrdersStatus,
} from "../../../redux/store/historicalOrders/Action";
import { useStoreWorkspace } from "../../storeSupport/StoreWorkspaceContext";
import { workspaceModel } from "../../storeSupport/workspaceModel";
import StorePageFrame from "./StorePageFrame";

const parseIsoDate = (value) => {
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const toIsoDate = (date) => {
  if (!(date instanceof Date)) {
    return "";
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateOnly = (value) => {
  if (!value) {
    return "N/A";
  }
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
    return text.slice(0, 10);
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "N/A";
  }
  return toIsoDate(parsed);
};

const formatRequestedAt = (value) => {
  if (!value) {
    return "N/A";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "N/A";
  }
  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const mapHistoricalStatus = (status) => {
  switch (status) {
    case "Successfull":
    case "BACKINTIME_COMPLETED":
      return { label: "Completed", tone: "success" };
    case "Error":
      return { label: "Error", tone: "critical" };
    case "Pending":
    case "BACKINTIME_STARTED":
      return { label: "Queued", tone: "warning" };
    case "ORDERS_PULL_STARTED":
      return { label: "Pull started", tone: "info" };
    case "ORDERS_PULL_IN_PROGRESS":
      return { label: "Pull in progress", tone: "info" };
    case "SYNC_IN_PROGRESS":
      return { label: "Sync in progress", tone: "info" };
    case "MERCHANT_INACTIVE":
      return { label: "Store connection unavailable", tone: "warning" };
    default:
      return { label: status || "Unknown", tone: "warning" };
  }
};

const HistoricalOrdersLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const currentShopId = String(id ?? "");
  const workspace = useStoreWorkspace();
  const capabilities = workspaceModel(
    workspace.data,
    workspace.shopId
  ).capabilities;
  const canWrite = capabilities?.canWrite === true;
  const routeShopId = useRef(currentShopId);
  routeShopId.current = currentShopId;
  const previewSequence = useRef(0);
  const queueSequence = useRef(0);
  const queueInFlight = useRef(false);

  const historicalState = useSelector((store) => store.requiredHistoricalData);
  const historicalStatus =
    historicalState?.shopId === currentShopId ? historicalState?.status : null;
  const statusState =
    historicalState?.shopId === currentShopId ? historicalState : null;
  const queueState =
    historicalState?.queue?.shopId === currentShopId
      ? historicalState.queue
      : null;
  const possibleOrdersState =
    historicalState?.possibleOrdersRequest?.shopId === currentShopId
      ? historicalState.possibleOrdersRequest
      : null;

  const rows = Array.isArray(historicalStatus?.storeBackInTimeTokens)
    ? historicalStatus.storeBackInTimeTokens
    : [];

  const defaultStartDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return toIsoDate(date);
  }, []);

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [datePickerActive, setDatePickerActive] = useState(false);
  const startDateAsDate = useMemo(() => parseIsoDate(startDate), [startDate]);
  const [month, setMonth] = useState(startDateAsDate.getMonth());
  const [year, setYear] = useState(startDateAsDate.getFullYear());
  const [confirmation, setConfirmation] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  useEffect(() => {
    previewSequence.current += 1;
    queueSequence.current += 1;
    queueInFlight.current = false;
    setConfirmation(null);
    setPreviewError(null);
  }, [currentShopId]);

  useEffect(() => {
    dispatch(historicalOrdersStatus({ page: 0, shopId: currentShopId }));
  }, [dispatch, currentShopId]);

  useEffect(() => {
    if (queueState?.isSuccess) {
      dispatch(historicalOrdersStatus({ page: 0, shopId: currentShopId }));
    }
  }, [dispatch, currentShopId, queueState?.isSuccess]);

  useEffect(() => {
    if (
      historicalStatus?.message === "Actual error message" ||
      statusState?.error === "Actual error message" ||
      queueState?.error === "Actual error message"
    ) {
      navigate("/logout");
    }
  }, [historicalStatus?.message, navigate, queueState?.error, statusState?.error]);

  const previewHistoricalRequest = async () => {
    if (!canWrite) return;
    const target = Object.freeze({
      shopId: currentShopId,
      date: startDate,
      requestId: ++previewSequence.current,
    });
    setConfirmation(null);
    setPreviewError(null);
    const result = await dispatch(
      gettotalPossibleOrders({ shopId: target.shopId, date: target.date })
    );
    if (
      routeShopId.current !== target.shopId ||
      previewSequence.current !== target.requestId
    ) {
      return;
    }
    if (!result?.ok) {
      setPreviewError(result?.error || "Eligible-order check failed");
      return;
    }
    const count = result.data?.count;
    if (!Number.isSafeInteger(count) || count <= 0) {
      setPreviewError("No eligible orders were found for this store and start date.");
      return;
    }
    setConfirmation(
      Object.freeze({ shopId: target.shopId, date: target.date, ordersCount: count })
    );
  };

  const confirmHistoricalRequest = async () => {
    const target = confirmation;
    if (
      !canWrite ||
      !target ||
      target.shopId !== routeShopId.current ||
      queueInFlight.current
    ) {
      return;
    }
    queueInFlight.current = true;
    const requestId = ++queueSequence.current;
    const result = await dispatch(
      historicalOrdersQueue({
        date: target.date,
        shopId: target.shopId,
        ordersCount: target.ordersCount,
        type: "USER",
      })
    );
    if (
      routeShopId.current === target.shopId &&
      queueSequence.current === requestId &&
      result?.ok
    ) {
      setConfirmation(null);
    }
    if (queueSequence.current === requestId) {
      queueInFlight.current = false;
    }
  };

  return (
    <StorePageFrame title="Historical orders" gap="24px">
        {!canWrite ? (
          <Banner
            title={
              workspace.loading && !workspace.data
                ? "Checking support permissions"
                : "Read-only support access"
            }
            status="info"
          >
            <p>
              {workspace.loading && !workspace.data
                ? "Historical-import creation will become available after operator permissions are confirmed."
                : "This identity can inspect historical requests but cannot create a new import job."}
            </p>
          </Banner>
        ) : null}
        {statusState?.isError ? (
          <Banner title="Historical-request history is unavailable" status="critical">
            <p>{statusState.error}</p>
          </Banner>
        ) : null}
        {previewError ? (
          <Banner title="Unable to prepare historical request" status="critical">
            <p>{previewError}</p>
          </Banner>
        ) : null}
        {queueState?.isError ? (
          <Banner title="Historical request was not created" status="critical">
            <p>{queueState.error}</p>
          </Banner>
        ) : null}
        {queueState?.isSuccess ? (
          <Banner title="Historical request created" status="success">
            <p>The USER request was accepted for store {currentShopId}.</p>
          </Banner>
        ) : null}
        <Modal
          open={Boolean(confirmation)}
          onClose={() => {
            if (!queueState?.isRequest) setConfirmation(null);
          }}
          title="Confirm historical-order request"
          primaryAction={{
            content: "Create USER request",
            onAction: confirmHistoricalRequest,
            loading: Boolean(queueState?.isRequest),
            disabled: Boolean(queueState?.isRequest || !canWrite),
          }}
          secondaryActions={[
            {
              content: "Cancel",
              onAction: () => setConfirmation(null),
              disabled: Boolean(queueState?.isRequest),
            },
          ]}
        >
          <Modal.Section>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <Text as="p">Store ID: {confirmation?.shopId}</Text>
              <Text as="p">Start date: {confirmation?.date}</Text>
              <Text as="p">Eligible orders: {confirmation?.ordersCount}</Text>
              <Text as="p">
                This creates a USER back-in-time job and uses one historical-request allowance.
              </Text>
            </div>
          </Modal.Section>
        </Modal>
        <Card>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Text as="h2" variant="headingMd">
              Sync orders from before you installed PalSync
            </Text>
            <Text as="p" variant="bodyMd">
              Start date
            </Text>
            <div style={{ width: "380px" }}>
              <Popover
                active={datePickerActive}
                autofocusTarget="first-node"
                preferredAlignment="left"
                preferredPosition="below"
                onClose={() => setDatePickerActive(false)}
                activator={
                  <div
                    role="button"
                    tabIndex={canWrite ? 0 : -1}
                    aria-disabled={!canWrite}
                    onClick={() => {
                      if (canWrite) setDatePickerActive((prev) => !prev);
                    }}
                    onKeyDown={(event) => {
                      if (
                        canWrite &&
                        (event.key === "Enter" || event.key === " ")
                      ) {
                        event.preventDefault();
                        setDatePickerActive((prev) => !prev);
                      }
                    }}
                  >
                    <TextField
                      label="Start date"
                      labelHidden
                      type="text"
                      autoComplete="off"
                      value={startDateAsDate.toLocaleDateString("en-GB")}
                      onChange={() => {}}
                      suffix={<Icon source={CalendarMinor} />}
                      disabled={!canWrite}
                    />
                  </div>
                }
              >
                <Box padding="300">
                  <DatePicker
                    month={month}
                    year={year}
                    selected={{ start: startDateAsDate, end: startDateAsDate }}
                    disableDatesAfter={new Date()}
                    onMonthChange={(nextMonth, nextYear) => {
                      setMonth(nextMonth);
                      setYear(nextYear);
                    }}
                    onChange={(selected) => {
                      const nextDate = selected.start;
                      previewSequence.current += 1;
                      setStartDate(toIsoDate(nextDate));
                      setMonth(nextDate.getMonth());
                      setYear(nextDate.getFullYear());
                      setDatePickerActive(false);
                      setConfirmation(null);
                      setPreviewError(null);
                    }}
                  />
                </Box>
              </Popover>
            </div>
            <div>
              <Button
                variant="primary"
                loading={Boolean(possibleOrdersState?.isRequest)}
                disabled={Boolean(
                  !canWrite ||
                    queueState?.isRequest ||
                    possibleOrdersState?.isRequest
                )}
                onClick={previewHistoricalRequest}
              >
                Get orders
              </Button>
            </div>
          </div>
        </Card>

        <Card>
          <div style={{ padding: "20px" }}>
            <Text as="h2" variant="headingMd">
              Completed sync requests
            </Text>
          </div>
          <Divider />
          <IndexTable
            resourceName={{ singular: "historical request", plural: "historical requests" }}
            itemCount={rows.length}
            loading={Boolean(statusState?.isRequest)}
            selectable={false}
            headings={[
              { title: "Requested at" },
              { title: "Orders from" },
              { title: "Orders up to" },
              { title: "Total orders found" },
              { title: "Status" },
              { title: "Details" },
            ]}
          >
            {rows.map((historical, index) => {
              const statusMeta = mapHistoricalStatus(historical?.status);
              return (
                <IndexTable.Row
                  id={String(historical?.id || index)}
                  key={String(historical?.id || index)}
                  position={index}
                >
                  <IndexTable.Cell>{formatRequestedAt(historical?.creationDate)}</IndexTable.Cell>
                  <IndexTable.Cell>{formatDateOnly(historical?.fromDate)}</IndexTable.Cell>
                  <IndexTable.Cell>{formatDateOnly(historical?.endDate)}</IndexTable.Cell>
                  <IndexTable.Cell>{historical?.total ?? 0}</IndexTable.Cell>
                  <IndexTable.Cell>
                    <Badge tone={statusMeta.tone}>{statusMeta.label}</Badge>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    {historical?.message ||
                      historical?.statusDescription ||
                      "No additional details"}
                  </IndexTable.Cell>
                </IndexTable.Row>
              );
            })}
          </IndexTable>
        </Card>
    </StorePageFrame>
  );
};

export default HistoricalOrdersLayout;
